import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [activeSection, setActiveSection] = useState('');

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );
    document.querySelectorAll('section[id]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900">Article not found</h1>
          <a href="/" className="mt-4 inline-block text-primary-600 hover:underline">{t('breadcrumb_home')}</a>
        </div>
      </div>
    );
  }

  const title = currentLang === 'pt' ? article.title : currentLang === 'en' ? article.titleEn : article.titleDe;
  const articleDesc = currentLang === 'pt' ? article.description : currentLang === 'en' ? article.descriptionEn : article.descriptionDe;

  const tocItems = [
    { id: 'intro', label: currentLang === 'pt' ? 'Introdução' : currentLang === 'en' ? 'Introduction' : 'Einführung' },
    { id: 'what-is', label: currentLang === 'pt' ? 'O Que é CSSBuy Spreadsheet' : currentLang === 'en' ? 'What is CSSBuy Spreadsheet' : 'Was ist CSSBuy Spreadsheet' },
    { id: 'how-to-use', label: currentLang === 'pt' ? 'Como Usar' : currentLang === 'en' ? 'How to Use' : 'Wie man es verwendet' },
    { id: 'categories', label: currentLang === 'pt' ? 'Categorias Populares' : currentLang === 'en' ? 'Popular Categories' : 'Beliebte Kategorien' },
    { id: 'safety', label: currentLang === 'pt' ? 'Dicas de Segurança' : currentLang === 'en' ? 'Safety Tips' : 'Sicherheitstipps' },
    { id: 'faq', label: 'FAQ' },
    { id: 'related', label: currentLang === 'pt' ? 'Artigos Relacionados' : currentLang === 'en' ? 'Related Articles' : 'Verwandte Artikel' },
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const ptContent = {
    intro: 'O CSSBuy Spreadsheet tornou-se uma ferramenta essencial para quem busca produtos de forma organizada e eficiente. Neste guia completo, você vai aprender desde os conceitos básicos até as estratégias avançadas para aproveitar ao máximo esses recursos.',
    whatIs: 'Um CSSBuy Spreadsheet é uma planilha organizada que reúne links de produtos, informações de vendedores, preços, fotos de QC (Quality Control) e dados de envio em um único documento acessível. Essa abordagem permite que compradores comparem opções, verifiquem reputações e tomem decisões mais informadas sem precisar navegar por dezenas de páginas diferentes.',
    howToUse: [
      'Escolha uma categoria de produto que você deseja explorar.',
      'Abra o CSSBuy Spreadsheet correspondente e familiarize-se com a estrutura das colunas.',
      'Verifique as informações do vendedor, incluindo reputação e avaliações.',
      'Analise as fotos de QC disponíveis para confirmar a qualidade do produto.',
      'Compare preços entre diferentes vendedores listados na planilha.',
      'Consulte as informações de envio e escolha a melhor opção para seu país.',
      'Siga o link do produto para o site de compra e finalize sua aquisição com segurança.',
    ],
    categories: 'As categorias mais populares nos CSSBuy Spreadsheets incluem tênis e sneakers, roupas streetwear, hoodies, camisetas, jaquetas, calças, bonés e acessórios. Cada categoria possui planilhas especializadas com vendedores reconhecidos e produtos atualizados regularmente.',
    safety: 'Ao usar CSSBuy Spreadsheets, siga sempre estas recomendações de segurança: verifique se os links são legítimos antes de clicar, confirme a reputação do vendedor em fóruns de comunidade, nunca compartilhe dados pessoais em planilhas públicas, analise cuidadosamente as fotos de QC antes de confirmar a compra, e compare preços com outras fontes para evitar golpes.',
    faq: [
      { q: 'O que é um CSSBuy Spreadsheet?', a: 'É uma planilha organizada que reúne links de produtos, informações de vendedores, preços e fotos de QC em um único documento.' },
      { q: 'Como encontro um CSSBuy Spreadsheet?', a: 'Você pode encontrar planilhas através de comunidades online, fóruns especializados e sites de recursos como o nosso.' },
      { q: 'É seguro usar CSSBuy Spreadsheet?', a: 'Sim, desde que você verifique os links, confirme a reputação dos vendedores e siga as práticas de segurança recomendadas.' },
      { q: 'Quais categorias estão disponíveis?', a: 'As categorias mais comuns incluem tênis, roupas, acessórios, bolsas, eletrônicos e itens de lifestyle.' },
    ],
  };

  const enContent = {
    intro: 'The CSSBuy Spreadsheet has become an essential tool for those seeking products in an organized and efficient manner. In this complete guide, you will learn everything from basic concepts to advanced strategies for making the most of these resources.',
    whatIs: 'A CSSBuy Spreadsheet is an organized document that brings together product links, seller information, prices, QC (Quality Control) photos and shipping data in a single accessible file. This approach allows buyers to compare options, verify reputations and make more informed decisions without having to navigate dozens of different pages.',
    howToUse: [
      'Choose a product category you want to explore.',
      'Open the corresponding CSSBuy Spreadsheet and familiarize yourself with the column structure.',
      'Check the seller information, including reputation and reviews.',
      'Analyze the available QC photos to confirm product quality.',
      'Compare prices between different sellers listed in the spreadsheet.',
      'Consult the shipping information and choose the best option for your country.',
      'Follow the product link to the purchase site and complete your acquisition safely.',
    ],
    categories: 'The most popular categories in CSSBuy Spreadsheets include sneakers and shoes, streetwear clothing, hoodies, t-shirts, jackets, pants, caps and accessories. Each category has specialized spreadsheets with recognized sellers and regularly updated products.',
    safety: 'When using CSSBuy Spreadsheets, always follow these safety recommendations: verify that links are legitimate before clicking, confirm the seller\'s reputation on community forums, never share personal data in public spreadsheets, carefully analyze QC photos before confirming the purchase, and compare prices with other sources to avoid scams.',
    faq: [
      { q: 'What is a CSSBuy Spreadsheet?', a: 'It is an organized spreadsheet that brings together product links, seller information, prices and QC photos in a single document.' },
      { q: 'How do I find a CSSBuy Spreadsheet?', a: 'You can find spreadsheets through online communities, specialized forums and resource sites like ours.' },
      { q: 'Is it safe to use CSSBuy Spreadsheet?', a: 'Yes, as long as you verify the links, confirm the sellers\' reputation and follow recommended security practices.' },
      { q: 'What categories are available?', a: 'The most common categories include shoes, clothing, accessories, bags, electronics and lifestyle items.' },
    ],
  };

  const deContent = {
    intro: 'Das CSSBuy Spreadsheet ist zu einem unverzichtbaren Werkzeug für alle geworden, die Produkte auf organisierte und effiziente Weise suchen. In dieser vollständigen Anleitung lernen Sie alles von den Grundlagen bis zu fortgeschrittenen Strategien, um diese Ressourcen optimal zu nutzen.',
    whatIs: 'Ein CSSBuy Spreadsheet ist ein organisiertes Dokument, das Produktlinks, Verkäuferinformationen, Preise, QC-Fotos (Qualitätskontrolle) und Versanddaten in einer einzigen zugänglichen Datei zusammenführt. Dieser Ansatz ermöglicht es Käufern, Optionen zu vergleichen, Reputationen zu überprüfen und fundiertere Entscheidungen zu treffen, ohne Dutzende verschiedener Seiten durchsuchen zu müssen.',
    howToUse: [
      'Wählen Sie eine Produktkategorie, die Sie erkunden möchten.',
      'Öffnen Sie das entsprechende CSSBuy Spreadsheet und machen Sie sich mit der Spaltenstruktur vertraut.',
      'Überprüfen Sie die Verkäuferinformationen, einschließlich Reputation und Bewertungen.',
      'Analysieren Sie die verfügbaren QC-Fotos, um die Produktqualität zu bestätigen.',
      'Vergleichen Sie die Preise zwischen verschiedenen im Spreadsheet aufgeführten Verkäufern.',
      'Konsultieren Sie die Versandinformationen und wählen Sie die beste Option für Ihr Land.',
      'Folgen Sie dem Produktlink zur Kaufseite und schließen Sie Ihren Kauf sicher ab.',
    ],
    categories: 'Die beliebtesten Kategorien in CSSBuy Spreadsheets umfassen Sneakers und Schuhe, Streetwear-Bekleidung, Hoodies, T-Shirts, Jacken, Hosen, Caps und Accessoires. Jede Kategorie hat spezialisierte Spreadsheets mit anerkannten Verkäufern und regelmäßig aktualisierten Produkten.',
    safety: 'Bei der Verwendung von CSSBuy Spreadsheets befolgen Sie immer diese Sicherheitsempfehlungen: Überprüfen Sie, ob Links legitim sind, bevor Sie klicken, bestätigen Sie die Reputation des Verkäufers in Community-Foren, teilen Sie niemals persönliche Daten in öffentlichen Spreadsheets, analysieren Sie QC-Fotos sorgfältig, bevor Sie den Kauf bestätigen, und vergleichen Sie Preise mit anderen Quellen, um Betrug zu vermeiden.',
    faq: [
      { q: 'Was ist ein CSSBuy Spreadsheet?', a: 'Es ist ein organisiertes Spreadsheet, das Produktlinks, Verkäuferinformationen, Preise und QC-Fotos in einem einzigen Dokument zusammenführt.' },
      { q: 'Wie finde ich ein CSSBuy Spreadsheet?', a: 'Sie können Spreadsheets über Online-Communities, spezialisierte Foren und Ressourcen-Websites wie unsere finden.' },
      { q: 'Ist es sicher, CSSBuy Spreadsheet zu verwenden?', a: 'Ja, solange Sie die Links überprüfen, die Reputation der Verkäufer bestätigen und empfohlene Sicherheitspraktiken befolgen.' },
      { q: 'Welche Kategorien sind verfügbar?', a: 'Die gängigsten Kategorien umfassen Schuhe, Bekleidung, Accessoires, Taschen, Elektronik und Lifestyle-Artikel.' },
    ],
  };

  const c = currentLang === 'pt' ? ptContent : currentLang === 'en' ? enContent : deContent;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 md:mb-8">
          <a href="/" className="hover:text-primary-600 transition-colors">{t('breadcrumb_home')}</a>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <a href="/#articles" className="hover:text-primary-600 transition-colors">{t('nav_guides')}</a>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
          <span className="text-foreground-600 font-medium">{title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24 bg-background-100 border border-background-200/70 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-4">{t('toc_title')}</h3>
              <ul className="space-y-2">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollTo(item.id)}
                      className={`text-sm text-left w-full transition-colors ${activeSection === item.id ? 'text-primary-600 font-medium' : 'text-foreground-500 hover:text-primary-600'}`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Related in sidebar */}
            <div className="sticky top-auto mt-6 bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-foreground-900 uppercase tracking-wide mb-3">{t('related_articles')}</h3>
              <ul className="space-y-2.5">
                {articles.filter((a) => a.slug !== slug).slice(0, 4).map((a) => (
                  <li key={a.slug}>
                    <a href={`/article/${a.slug}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors leading-snug block">
                      {currentLang === 'pt' ? a.title : currentLang === 'en' ? a.titleEn : a.titleDe}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9">
            {/* Article Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center" />
                  {t('article_updated')}: {article.updatedDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center" />
                  {article.readingTime} {t('article_reading_time')}
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 tracking-tight leading-tight">
                {title}
              </h1>
              <div className="mt-4 rounded-xl overflow-hidden border border-background-200/70">
                <img
                  src={article.image}
                  alt={title}
                  className="w-full h-48 md:h-72 object-cover"
                />
              </div>
            </div>

            {/* Introduction */}
            <section id="intro" className="mb-10 md:mb-12">
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {articleDesc}
              </p>
              <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed">
                {c.intro}
              </p>
            </section>

            {/* What Is */}
            <section id="what-is" className="mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                {tocItems[1].label}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {c.whatIs}
              </p>
            </section>

            {/* How to Use */}
            <section id="how-to-use" className="mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                {tocItems[2].label}
              </h2>
              <ol className="space-y-3">
                {c.howToUse.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-bold mt-0.5">
                      {i + 1}
                    </span>
                    <span className="text-sm md:text-base text-foreground-600 leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            {/* Categories */}
            <section id="categories" className="mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                {tocItems[3].label}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {c.categories}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {['Shoes', 'Hoodies', 'T-Shirts', 'Jackets', 'Pants', 'Accessories', 'Bags', 'Jersey', 'Perfumes'].map((cat) => (
                  <a
                    key={cat}
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2.5 text-sm text-center bg-background-100 border border-background-200/70 rounded-lg text-foreground-600 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200/70 transition-colors"
                  >
                    {cat}
                  </a>
                ))}
              </div>
            </section>

            {/* Safety */}
            <section id="safety" className="mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                {tocItems[4].label}
              </h2>
              <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-5 md:p-6">
                <div className="flex items-start gap-3">
                  <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center text-accent-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-accent-800 mb-1">{t('safety_note')}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{c.safety}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">FAQ</h2>
              <div className="space-y-3">
                {c.faq.map((item, i) => (
                  <div key={i} className="bg-background-100 border border-background-200/70 rounded-xl p-4 md:p-5">
                    <h3 className="text-sm font-semibold text-foreground-800 mb-2">{item.q}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Articles */}
            <section id="related" className="mb-10 md:mb-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                {t('related_articles')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {articles.filter((a) => a.slug !== slug).map((a) => {
                  const relTitle = currentLang === 'pt' ? a.title : currentLang === 'en' ? a.titleEn : a.titleDe;
                  return (
                    <a
                      key={a.slug}
                      href={`/article/${a.slug}`}
                      className="group flex items-start gap-3 bg-background-100 border border-background-200/70 rounded-xl p-4 hover:border-primary-200/70 transition-colors"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={a.image} alt={relTitle} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors leading-snug">{relTitle}</h3>
                        <span className="mt-1 inline-flex items-center gap-1 text-xs text-primary-600">
                          {t('article_read_guide')}
                          <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            </section>

            {/* CTA */}
            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-8 text-center">
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-2">
                {t('final_cta_h2')}
              </h3>
              <p className="text-sm text-foreground-500 mb-5">
                {t('final_cta_desc')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-block px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('final_cta_primary')}
              </a>
            </div>

            {/* Internal link back to home */}
            <div className="mt-8 text-center">
              <a href="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors">
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                {t('breadcrumb_home')}
              </a>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}